import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import classNames from 'classnames';

import IconArrowHead from '../../IconArrowHead/IconArrowHead';
import IconCheckmark from '../../IconCheckmark/IconCheckmark';
import useIsMobile from '../../../hooks/useIsMobile';
import { CATEGORY_STATE } from './categoryTypeSelection';

import dropdownCss from '../../DropdownMultiEnum/DropdownMultiEnum.module.css';
import css from './NestedCategoryDropdown.module.css';

const MOBILE_BREAKPOINT = 768;
const DRAWER_VARIANT = 'drawer';
// Long enough to cross the gap between a row and its flyout diagonally.
const FLYOUT_CLOSE_DELAY_MS = 150;

// A category with a single type (sloep, jetski) offers nothing to narrow to.
const hasSubTypes = category => category.types.length > 1;

const includesSearch = (label, search) =>
  label?.toLowerCase().includes(search.trim().toLowerCase());

const CheckState = ({ state }) =>
  state === CATEGORY_STATE.WHOLE || state === true ? (
    <IconCheckmark size="small" className={dropdownCss.checkIcon} />
  ) : state === CATEGORY_STATE.PARTIAL ? (
    <span className={css.partialIcon} aria-hidden />
  ) : null;

const ariaChecked = state =>
  state === CATEGORY_STATE.WHOLE ? 'true' : state === CATEGORY_STATE.PARTIAL ? 'mixed' : 'false';

/**
 * A category list whose categories open a sub-list of their types: a flyout on
 * hover (or ArrowRight) on desktop, an inline expansion in the mobile drawer.
 *
 * Presentational only. Which rows are ticked and what a click does come from
 * the caller, so the selection rules stay in categoryTypeSelection.js.
 *
 * Looks like DropdownMultiEnum on purpose (it reuses that module's classes), but
 * is a separate component because that one is a flat list shared by form fields.
 */
const NestedCategoryDropdown = props => {
  const {
    categories,
    placeholder,
    selectionCount,
    getCategoryState,
    isTypeSelected,
    onCategoryClick,
    onTypeClick,
    onToggle,
    variant,
    className,
  } = props;

  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  const isDrawer = variant === DRAWER_VARIANT || isMobile;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Desktop: the category whose flyout is showing, and where to draw it.
  const [flyoutCategory, setFlyoutCategory] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  // Mobile: categories expanded inline.
  const [expanded, setExpanded] = useState([]);
  // Keyboard position: a row in the main list, then optionally a row in the flyout.
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeTypeIndex, setActiveTypeIndex] = useState(-1);

  const rootRef = useRef(null);
  const rowRefs = useRef({});
  const closeTimer = useRef(null);

  const isSearching = !isDrawer && search.trim().length > 0;

  // While searching, categories and types are listed flat, each as its own row.
  const searchRows = useMemo(() => {
    if (!isSearching) return [];
    return categories.flatMap(category => [
      ...(includesSearch(category.label, search) ? [{ kind: 'category', category }] : []),
      ...category.types
        .filter(type => includesSearch(type.label, search))
        .map(type => ({ kind: 'type', category, type })),
    ]);
  }, [categories, isSearching, search]);

  const groupedCategories = useMemo(
    () =>
      Object.entries(
        categories.reduce((groups, category, index) => {
          const group = category.group || 'default';
          return { ...groups, [group]: [...(groups[group] || []), { ...category, index }] };
        }, {})
      ),
    [categories]
  );

  const flyout = categories.find(c => c.value === flyoutCategory);

  const cancelFlyoutClose = () => clearTimeout(closeTimer.current);
  const scheduleFlyoutClose = () => {
    cancelFlyoutClose();
    closeTimer.current = setTimeout(() => {
      setFlyoutCategory(null);
      setActiveTypeIndex(-1);
    }, FLYOUT_CLOSE_DELAY_MS);
  };

  const openFlyout = useCallback(category => {
    cancelFlyoutClose();
    const row = rowRefs.current[category.value];
    const root = rootRef.current;
    if (row && root) {
      setFlyoutTop(row.getBoundingClientRect().top - root.getBoundingClientRect().top);
    }
    setFlyoutCategory(hasSubTypes(category) ? category.value : null);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
    setFlyoutCategory(null);
    setActiveIndex(-1);
    setActiveTypeIndex(-1);
  }, []);

  const toggleOpen = () => {
    if (open) {
      close();
    } else {
      setOpen(true);
    }
    onToggle?.();
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Click outside and keyboard navigation, while open.
  useEffect(() => {
    if (!open) return undefined;

    const onDocumentClick = e => {
      if (!rootRef.current?.contains(e.target)) close();
    };

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        close();
        return;
      }

      if (isSearching) {
        const last = searchRows.length - 1;
        if (e.key === 'ArrowDown') setActiveIndex(i => Math.min(i + 1, last));
        else if (e.key === 'ArrowUp') setActiveIndex(i => Math.max(i - 1, 0));
        else if (e.key === 'Enter' && searchRows[activeIndex]) {
          const row = searchRows[activeIndex];
          row.kind === 'category'
            ? onCategoryClick(row.category.value)
            : onTypeClick(row.type.value);
        } else return;
        e.preventDefault();
        return;
      }

      if (isDrawer) return;

      const inFlyout = flyout && activeTypeIndex >= 0;
      if (inFlyout) {
        const last = flyout.types.length - 1;
        if (e.key === 'ArrowDown') setActiveTypeIndex(i => Math.min(i + 1, last));
        else if (e.key === 'ArrowUp') setActiveTypeIndex(i => Math.max(i - 1, 0));
        else if (e.key === 'ArrowLeft') setActiveTypeIndex(-1);
        else if (e.key === 'Enter') onTypeClick(flyout.types[activeTypeIndex].value);
        else return;
        e.preventDefault();
        return;
      }

      const last = categories.length - 1;
      const active = categories[activeIndex];
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const next =
          e.key === 'ArrowDown' ? Math.min(activeIndex + 1, last) : Math.max(activeIndex - 1, 0);
        setActiveIndex(next);
        setFlyoutCategory(null);
      } else if (e.key === 'ArrowRight' && active && hasSubTypes(active)) {
        openFlyout(active);
        setActiveTypeIndex(0);
      } else if (e.key === 'Enter' && active) {
        onCategoryClick(active.value);
      } else return;
      e.preventDefault();
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [
    open,
    close,
    isSearching,
    isDrawer,
    searchRows,
    activeIndex,
    activeTypeIndex,
    flyout,
    categories,
    openFlyout,
    onCategoryClick,
    onTypeClick,
  ]);

  const displayText = `${placeholder}${selectionCount > 0 ? ` (${selectionCount})` : ''}`;

  const renderTrigger = () => {
    const inputClasses = classNames(dropdownCss.input, {
      [dropdownCss.inputMobile]: isMobile,
      [dropdownCss.inputDrawer]: variant === DRAWER_VARIANT,
    });

    return (
      <div className={dropdownCss.dropdownTrigger}>
        <div
          className={classNames(dropdownCss.inputWrapper, {
            [dropdownCss.inputWrapperOpen]: isMobile && open,
          })}
        >
          {isDrawer ? (
            <button
              type="button"
              className={inputClasses}
              onClick={toggleOpen}
              aria-haspopup="tree"
              aria-expanded={open}
            >
              <span className={dropdownCss.inputValue} aria-hidden>
                {displayText}
              </span>
            </button>
          ) : (
            <input
              className={inputClasses}
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setActiveIndex(-1);
                setFlyoutCategory(null);
              }}
              onFocus={() => {
                if (!open) {
                  setOpen(true);
                  onToggle?.();
                }
              }}
              placeholder={displayText}
              aria-haspopup="tree"
              aria-expanded={open}
            />
          )}

          <button
            type="button"
            className={dropdownCss.openBtn}
            onClick={toggleOpen}
            aria-label={open ? 'Close options' : 'Open options'}
          >
            <IconArrowHead
              direction={isMobile ? (open ? 'left' : 'right') : open ? 'up' : 'down'}
            />
          </button>
        </div>
      </div>
    );
  };

  const renderTypeRow = (type, { active, hint } = {}) => {
    const checked = isTypeSelected(type.value);
    return (
      <li
        key={type.value}
        role="treeitem"
        aria-checked={checked ? 'true' : 'false'}
        className={classNames(dropdownCss.option, { [css.activeRow]: active })}
        onClick={e => {
          e.stopPropagation();
          onTypeClick(type.value);
        }}
      >
        <CheckState state={checked} />
        <span className={css.rowLabel}>{type.label}</span>
        {hint && <span className={css.categoryHint}>{hint}</span>}
      </li>
    );
  };

  const renderCategoryRow = category => {
    const state = getCategoryState(category.value);
    const withSubTypes = hasSubTypes(category);
    const isExpanded = expanded.includes(category.value);
    const active = !isSearching && category.index === activeIndex && activeTypeIndex < 0;

    return (
      <li
        key={category.value}
        role="treeitem"
        aria-checked={ariaChecked(state)}
        aria-expanded={
          withSubTypes ? (isDrawer ? isExpanded : flyoutCategory === category.value) : undefined
        }
        className={css.categoryItem}
      >
        <div
          ref={el => (rowRefs.current[category.value] = el)}
          className={classNames(dropdownCss.option, css.categoryRow, {
            [css.activeRow]: active || (!isDrawer && flyoutCategory === category.value),
          })}
          onClick={() => onCategoryClick(category.value)}
          onMouseEnter={() => {
            if (isDrawer) return;
            setActiveIndex(category.index);
            setActiveTypeIndex(-1);
            openFlyout(category);
          }}
          onMouseLeave={() => !isDrawer && scheduleFlyoutClose()}
        >
          <CheckState state={state} />
          <span className={css.rowLabel}>{category.label}</span>
          {withSubTypes && !isDrawer && (
            <span className={css.flyoutArrow} aria-hidden>
              <IconArrowHead direction="right" />
            </span>
          )}
          {withSubTypes && isDrawer && (
            <button
              type="button"
              className={css.expandBtn}
              aria-label={isExpanded ? 'Collapse types' : 'Expand types'}
              onClick={e => {
                e.stopPropagation();
                setExpanded(prev =>
                  isExpanded ? prev.filter(v => v !== category.value) : [...prev, category.value]
                );
              }}
            >
              <IconArrowHead direction={isExpanded ? 'up' : 'down'} />
            </button>
          )}
        </div>

        {withSubTypes && isDrawer && isExpanded && (
          <ul className={css.subList} role="group">
            {category.types.map(type => renderTypeRow(type))}
          </ul>
        )}
      </li>
    );
  };

  const renderList = () => {
    if (isSearching) {
      return searchRows.map((row, index) =>
        row.kind === 'category' ? (
          <li
            key={`category:${row.category.value}`}
            role="treeitem"
            aria-checked={ariaChecked(getCategoryState(row.category.value))}
            className={classNames(dropdownCss.option, { [css.activeRow]: index === activeIndex })}
            onClick={() => onCategoryClick(row.category.value)}
          >
            <CheckState state={getCategoryState(row.category.value)} />
            <span className={css.rowLabel}>{row.category.label}</span>
          </li>
        ) : (
          renderTypeRow(row.type, { active: index === activeIndex, hint: row.category.label })
        )
      );
    }

    return groupedCategories.map(([group, groupCategories]) => (
      <React.Fragment key={group}>
        {group !== 'default' && <li className={dropdownCss.group} role="separator" />}
        {groupCategories.map(renderCategoryRow)}
      </React.Fragment>
    ));
  };

  return (
    <div
      className={classNames(dropdownCss.dropdown, className, { [dropdownCss.dropdownOpen]: open })}
      ref={rootRef}
    >
      {renderTrigger()}
      {isDrawer && <div className={dropdownCss.divider} />}

      {open && (
        <ul
          className={classNames(dropdownCss.optionsList, {
            [dropdownCss.drawerVariantList]: variant === DRAWER_VARIANT,
          })}
          role="tree"
          aria-multiselectable="true"
          // The flyout is drawn outside the scrolling list, so it would drift
          // from its row; closing it on scroll is simpler than tracking it.
          onScroll={() => setFlyoutCategory(null)}
        >
          {renderList()}
        </ul>
      )}

      {open && !isDrawer && !isSearching && flyout && (
        <ul
          className={css.flyout}
          style={{ top: flyoutTop }}
          role="group"
          aria-label={flyout.label}
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
        >
          {flyout.types.map((type, index) =>
            renderTypeRow(type, { active: index === activeTypeIndex })
          )}
        </ul>
      )}
    </div>
  );
};

const typeShape = shape({ value: string.isRequired, label: string.isRequired });

NestedCategoryDropdown.propTypes = {
  categories: arrayOf(
    shape({
      value: string.isRequired,
      label: string.isRequired,
      group: string,
      types: arrayOf(typeShape).isRequired,
    })
  ).isRequired,
  placeholder: string.isRequired,
  selectionCount: number.isRequired,
  getCategoryState: func.isRequired,
  isTypeSelected: func.isRequired,
  onCategoryClick: func.isRequired,
  onTypeClick: func.isRequired,
  onToggle: func,
  variant: string,
  className: string,
};

export default NestedCategoryDropdown;
